import React, { useEffect, useRef, useState } from 'react';
import { BAND_COLORS } from '../../helpers';
import type { RelativizeDate } from '../../helpers';

/**
 * v3 — hand-rolled SVG renderer (no charting library), built so EVERY x position
 * is arithmetic we control. The whole point: a player's exposure for a period is
 * held across the price SEGMENT, so each exposure column is centred on the
 * segment between two price points (not on a point). With N points over a plot of
 * width plotW and slot = plotW / N:
 *   • price point i  → x = m + (i + 0.5)·slot       (centre of slot i)
 *   • segment k→k+1  → x = m + (k + 1)·slot          = midpoint of point k & k+1
 * so column k (the exposure recorded at date k, held into k+1) lands exactly on
 * the k→k+1 segment. Regime bands span their segment, so band + column coincide.
 */

type Pt = { price: number | null; exposure: number; label: string };

const GREEN_BG = 'rgba(14, 159, 110, 0.18)';
const RED_BG = 'rgba(209, 67, 67, 0.18)';
const INK = '#1f2d44';
const BLUE = '#0f5fd6';
const ORANGE = '#d48806';
const MUTED = '#5c6b7f';
const MUTED2 = '#9aa7b8';

const priceFmt = (v: number) => v.toFixed(2);
const expoFmt = (v: number) => Math.round(v).toLocaleString();
const fmtDate = (iso: string, withTime = false) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
  // Real-time intraday bars share a calendar date, so append HH:MM to keep the
  // tooltip distinguishable. Historical / mock (withTime=false) is unchanged.
  return withTime
    ? `${date} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
    : date;
};

// Observe the container's pixel size so we can render in real px (crisp, no
// SVG aspect-ratio distortion) and recompute on resize.
const useSize = (ref: React.RefObject<HTMLElement>) => {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const r = el.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
};

const sign = (v: number) => (v > 0 ? 1 : v < 0 ? -1 : 0);

const SplitBodyV3: React.FC<{
  points: Pt[];
  // Self + historical only: relativises the tooltip date to "Day N". Absent →
  // the real "04 May 18" date (trainer / real-time), unchanged.
  relativizeDate?: RelativizeDate;
  // Real-time only: append HH:MM to the tooltip date (intraday bars share a day).
  intraday?: boolean;
}> = ({ points, relativizeDate, intraday }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { w, h } = useSize(ref);
  const [hover, setHover] = useState<number | null>(null);

  const N = points.length;
  const ready = w > 4 && h > 4 && N > 0;

  let svg: React.ReactNode = null;
  let tip: React.ReactNode = null;

  if (ready) {
    // --- horizontal geometry (the alignment guarantee) ---
    const m = 8; // outer padding
    const axisW = 42; // left gutter reserved for the price y-axis labels
    const rAxis = 40; // right gutter reserved for the long/short labels
    const plotW = Math.max(w - axisW - rAxis, 1);
    const plotRight = axisW + plotW; // right edge of the plotting area
    const slot = plotW / N;
    // Both the price line and the exposure columns share `axisW` as the left
    // origin, so reserving the gutters never disturbs their alignment.
    const xPoint = (i: number) => axisW + (i + 0.5) * slot;
    const xSeg = (k: number) => axisW + (k + 1) * slot; // midpoint of point k & k+1

    // --- vertical bands ---
    const priceTop = 22;
    const priceBot = h * 0.5;
    const priceH = Math.max(priceBot - priceTop, 1);
    const expoLabelY = h * 0.59;
    const expoTop = h * 0.65;
    const expoBot = h - 6;
    const expoH = Math.max(expoBot - expoTop, 1);
    const zeroY = expoTop + expoH / 2;

    // --- price scale ---
    const pvals = points
      .map(p => p.price)
      .filter((v): v is number => v != null);
    const pMin = pvals.length ? Math.min(...pvals) : 0;
    const pMax = pvals.length ? Math.max(...pvals) : 1;
    const yPrice = (v: number) =>
      pMax === pMin
        ? (priceTop + priceBot) / 2
        : priceBot - ((v - pMin) / (pMax - pMin)) * priceH;

    // Nice round price ticks for the y-axis (≤ count ticks within [pMin, pMax]).
    const niceTicks = (lo: number, hi: number, count = 4) => {
      if (!(hi > lo)) return [lo];
      const raw = (hi - lo) / (count - 1);
      const mag = Math.pow(10, Math.floor(Math.log10(raw)));
      const norm = raw / mag;
      const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
      const ticks: number[] = [];
      for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step)
        ticks.push(v);
      return ticks;
    };
    const yTicks = niceTicks(pMin, pMax, 4);

    // --- exposure scale (symmetric around the zero line) ---
    const maxAbs = Math.max(1, ...points.map(p => Math.abs(p.exposure)));
    const barLen = (v: number) => (Math.abs(v) / maxAbs) * (expoH / 2 - 2);

    // --- regime runs over SEGMENTS (k = 0 .. N-2): rode vs fought ---
    const segSign = (k: number) => {
      const a = points[k].price;
      const b = points[k + 1].price;
      if (a == null || b == null) return 0;
      return sign(points[k].exposure) * sign(b - a);
    };
    const runs: { a: number; b: number; s: number }[] = [];
    for (let k = 0; k < N - 1; ) {
      const s = segSign(k);
      if (s === 0) {
        k++;
        continue;
      }
      let j = k;
      while (j + 1 < N - 1 && segSign(j + 1) === s) j++;
      runs.push({ a: k, b: j, s });
      k = j + 1;
    }

    const priceLine = points
      .map((p, i) =>
        p.price == null ? null : `${xPoint(i)},${yPrice(p.price)}`,
      )
      .filter(Boolean)
      .join(' ');

    svg = (
      <svg
        width={w}
        height={h}
        style={{ display: 'block' }}
        onMouseMove={e => {
          const r = ref.current?.getBoundingClientRect();
          if (!r) return;
          const f = (e.clientX - r.left - axisW) / slot; // 0..N (plot starts at axisW)
          setHover(Math.max(0, Math.min(N - 1, Math.round(f - 0.5))));
        }}
        onMouseLeave={() => setHover(null)}
      >
        {/* regime bands — each spans exactly its price segment(s) */}
        {runs.map((r, i) => (
          <rect
            key={`band-${i}`}
            x={xPoint(r.a)}
            y={priceTop}
            width={xPoint(r.b + 1) - xPoint(r.a)}
            height={priceH}
            fill={r.s > 0 ? GREEN_BG : RED_BG}
          />
        ))}

        {/* price y-axis: faint gridlines + value labels in the left gutter */}
        {yTicks.map((t, i) => (
          <g key={`yt-${i}`}>
            <line
              x1={axisW}
              x2={plotRight}
              y1={yPrice(t)}
              y2={yPrice(t)}
              stroke="#eef2f8"
              strokeWidth={1}
            />
            <text
              x={axisW - 6}
              y={yPrice(t)}
              fontSize={10}
              fontWeight={600}
              fill={MUTED}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {priceFmt(t)}
            </text>
          </g>
        ))}

        {/* price line */}
        <polyline points={priceLine} fill="none" stroke={INK} strokeWidth={2} />

        {/* zero baseline for exposure */}
        <line
          x1={axisW}
          x2={plotRight}
          y1={zeroY}
          y2={zeroY}
          stroke="#e6ebf3"
          strokeWidth={1}
        />

        {/* exposure columns — centred on each segment midpoint */}
        {points.slice(0, N - 1).map((p, k) => {
          if (!p.exposure) return null;
          const len = barLen(p.exposure);
          const bw = slot * 0.66;
          const long = p.exposure >= 0;
          return (
            <rect
              key={`bar-${k}`}
              x={xSeg(k) - bw / 2}
              y={long ? zeroY - len : zeroY}
              width={bw}
              height={len}
              rx={1}
              fill={long ? BLUE : ORANGE}
            />
          );
        })}

        {/* hover guide */}
        {hover != null && (
          <line
            x1={xPoint(hover)}
            x2={xPoint(hover)}
            y1={priceTop}
            y2={expoBot}
            stroke={MUTED}
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}

        {/* section labels */}
        <text x={m} y={13} fontSize={11} fontWeight={600} fill={MUTED}>
          price &amp; regime
        </text>
        <text x={m + 92} y={13} fontSize={11} fill={MUTED2}>
          green: rode the wave · red: fought the regime
        </text>
        <text x={m} y={expoLabelY} fontSize={11} fontWeight={600} fill={MUTED}>
          player net exposure
        </text>
        <text
          x={w - m}
          y={expoTop + 9}
          fontSize={11}
          fontWeight={600}
          fill={MUTED}
          textAnchor="end"
        >
          long
        </text>
        <text
          x={w - m}
          y={expoBot}
          fontSize={11}
          fontWeight={600}
          fill={MUTED}
          textAnchor="end"
        >
          short
        </text>
      </svg>
    );

    if (hover != null) {
      const p = points[hover];
      const left = Math.max(4, Math.min(xPoint(hover) + 10, w - 170));
      tip = (
        <div
          style={{
            position: 'absolute',
            left,
            top: priceTop,
            pointerEvents: 'none',
            background: '#fff',
            border: '1px solid #e6ebf3',
            borderRadius: 6,
            boxShadow: '0 4px 14px rgba(19,33,68,0.12)',
            padding: '6px 9px',
            fontSize: 12,
            color: INK,
            whiteSpace: 'nowrap',
            zIndex: 2,
          }}
        >
          <div style={{ color: MUTED, marginBottom: 2 }}>
            {relativizeDate?.(p.label) ?? fmtDate(p.label, intraday)}
          </div>
          <div>
            <Dot color={INK} /> Price:{' '}
            <b>{p.price == null ? '–' : priceFmt(p.price)}</b>
          </div>
          <div>
            <Dot color={p.exposure >= 0 ? BLUE : ORANGE} /> Net exposure:{' '}
            <b>{expoFmt(p.exposure)}</b>
          </div>
        </div>
      );
    }
  }

  return (
    <div
      ref={ref}
      style={{ position: 'relative', flex: '1 1 auto', minHeight: 0 }}
    >
      {svg}
      {tip}
    </div>
  );
};

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <span
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: color,
      marginRight: 4,
    }}
  />
);

export default SplitBodyV3;
